import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { Observable, Subscription, firstValueFrom, forkJoin, map, of, switchMap } from 'rxjs';

import { CurrencyMaskConfig } from 'ng2-currency-mask';

import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { PedidoService } from 'src/app/core/services/http/pedido.service';
import { SistemaService } from 'src/app/core/services/http/sistema.service';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { TipoAtendimentoEnum } from '../../enums/tipo-atendimento.enum';
import { ApiResponse } from '../../interfaces/api-response.interface';
import { ContaPagamentoComandaResponse } from '../../models/conta-pagamento-comanda.model';
import { MetodosModal } from '../../interfaces/metodos-modal.interface';
import { FormaPgtoRequestV1, FormaPgtoResponse } from '../../models/forma-pgto.model';
import { PagarContaRequest, PedidoModel, PgtoPedidoResponse } from '../../models/pedido.model';
import { RateioContaModel } from '../../models/rateio-conta.model';
import { VendasDetalheModel } from '../../models/vendas-detalhe.model';
import { HelpersUtil } from '../../utils/helpers.util';
import { ModalResult } from '../../interfaces/modal-result.interface';
import { DialogStateUtil } from '../../utils/dialog-state.util';
import { LocalizarClientesComponent, LocalizarClientesDataClose } from '../localizar-clientes/localizar-clientes.component';
import { ModaisService } from 'src/app/core/services/behavior/modais.service';
import { CaixaService } from 'src/app/core/services/http/caixa.service';
import { CastModelUtils } from '../../utils/CastModelUtils';
import { ConfigPdvDeliveryResponse } from '../../models/configuracao.model';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { DeviceService } from 'src/app/core/services/behavior/device.service';
import { DatetimeService } from 'src/app/core/services/http/datetime.service';
import { ToastService } from 'src/app/core/services/behavior/toast.service';
import { LoggerService } from 'src/app/core/services/behavior/logger.service';
import { OperacaoPgtoEnum } from '../../enums/operacao-pgto.enum';

@Component({
    selector: 'app-pagar-conta',
    templateUrl: './pagar-conta.component.html',
    styleUrls: ['./pagar-conta.component.scss'],
})
export class PagarContaComponent implements ViewWillEnter, ViewWillLeave, MetodosModal {
    // ModalProps
    public readonly isAutoAtendimento = this.authService.isAutoAtendimento;
    public readonly isOperadorCaixa = this.authService.user.operadorCaixa;
    public readonly pedido: PedidoModel;

    @ViewChild('formaPgtoSel', { static: false }) private formaPgtoSel: ElementRef<HTMLSelectElement>;
    @ViewChild('desconto', { static: false }) private descontoInp: ElementRef<HTMLSelectElement>;
    @ViewChild('valorPagarInp', { static: false }) private valorPagarInp: ElementRef<HTMLInputElement>;

    private subs$: Subscription[] = [];

    public arrFormasPgto: FormaPgtoResponse[] = [];
    public arrPedidos: VendasDetalheModel[] = [];
    public arrPgtos: PgtoPedidoResponse[] = [];
    public arrQtdPessoas: number[] = Array.from(Array(35).keys()).filter(l => l > 0);
    public disableBtnFecharConta = false;
    public desativarEnvioPagamento = false;
    public formaPgto: number;
    public incluirOpcional$ = this.sistemaService.getConfiguracoes().pipe(map(l => l.success ? l.data.cobrarTaxaServico : false));
    public optionsMask: CurrencyMaskConfig = {
        prefix: '',
        thousands: '.',
        decimal: ',',
        align: 'right',
        allowNegative: false,
        precision: 2,
        suffix: ''
    };
    public rateioConta = new RateioContaModel({
        qtdPessoas: 1
    });
    public tipoAtendimento: TipoAtendimentoEnum;
    public valorPagar: number;
    codigoCaixa = 0;
    public configPedido: ConfigPdvDeliveryResponse = new ConfigPdvDeliveryResponse();

    constructor(
        private modalCtrl: ModalController,
        private _modaisService: ModaisService,
        private authService: AuthenticationService,
        private pedidoService: PedidoService,
        private sistemaService: SistemaService,
        private caixaService: CaixaService,
        private storageService: StorageService,
        private deviceService: DeviceService,
        private datetimeService: DatetimeService,
        private toastService: ToastService,
        private loggerService: LoggerService,
    ) {
        this.caixaService.verificarCaixaAberto().subscribe(dataR => {
            this.codigoCaixa = dataR.data.codigo;
        });

        this.carregarConfigPedido();
    }

    public get isTipoAtendimentoBalcao() {
        return this.tipoAtendimento === TipoAtendimentoEnum.BALCAO;
    }

    public get isTipoAtendimentoDelivery() {
        return this.tipoAtendimento === TipoAtendimentoEnum.DELIVERY;
    }

    public get isTipoAtendimentoMesa() {
        return this.tipoAtendimento === TipoAtendimentoEnum.MESA;
    }

    public get pagamentoInvalido() {
        return this.valorPagar > this.rateioConta.restantePagar;
    }

    public async cancel(): Promise<void> {
        await this.modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
        DialogStateUtil.dialogIsClosed();
    }

    public async confirm(): Promise<void> {
        await this.modalCtrl.dismiss(null, ModalRoleEnum.CONFIRM);
        DialogStateUtil.dialogIsClosed();
    }

    public async carregarConfigPedido() {
        const configSaved = await this.storageService.get('configPedido');

        if (configSaved !== null) {
            this.configPedido = configSaved;
        }
    }

    public enviarPgto(): void {
        if (!this.valorPagar || !this.isOperadorCaixa) return;

        if (this.pagamentoInvalido) {
            this.toastService.warning("Valor acima do total");
            return;
        }

        const numeroComanda = this.pedido.numeroComanda;

        const requestPagamento = new PagarContaRequest(numeroComanda, this.codigoCaixa, this.formaPgto, this.valorPagar);
        const requestDesconto = new PagarContaRequest(numeroComanda, this.codigoCaixa, this.formaPgto, this.rateioConta.desconto);

        const desconto$ = this.rateioConta.desconto > 0 ? this.pedidoService.aplicarDesconto(requestDesconto) : of(null);

        desconto$
            .pipe(
                switchMap((resDesconto) => {
                    if (resDesconto && resDesconto.success) {
                        this.adicionarPgtoLocalmente(resDesconto.data, requestDesconto, true);
                        this.resetarDesconto();
                    }

                    return this.pedidoService.pagarConta(requestPagamento);
                })
            )
            .subscribe({
                next: async (res) => {
                    if (!res?.success) return;

                    this.adicionarPgtoLocalmente(res.data, requestPagamento);
                    this.sincronizarRateioConta();

                    this.valorPagar = this.rateioConta.restantePagar;
                    const fecharConta = this.rateioConta.restantePagar.toFixed(1) === '0.0';

                    if (fecharConta) {
                        this.loggerService.log("Restante a pagar é <= 0. Abrindo fechar conta.");
                        HelpersUtil.executeAfterDelay(async () => await this.fecharContaAsync(), 700);
                    } else {
                        this.setFocusInFormaPgtoSel();
                    }
                },
                error: (err) => {
                    this.loggerService.error('Erro no fluxo de pagamento', err);
                }
            });
    }

    public excluirPgto(codigoVzc: number, index: number) {
        this.pedidoService.excluirPgto(codigoVzc).subscribe(res => {
            if (res) {
                this.arrPgtos.splice(index, 1);
                this.sincronizarRateioConta();

                this.valorPagar = this.rateioConta.restantePagar;
            }
        });
    }

    public async fecharContaAsync() {
        const pgtoEmDinheiro = this.arrPgtos.filter(l => l.formaPgto == "Dinheiro" && l.operacao != OperacaoPgtoEnum.Desconto);

        if (this.tipoAtendimento !== TipoAtendimentoEnum.DELIVERY && pgtoEmDinheiro.length > 0) {
            const total = HelpersUtil.sumArray(pgtoEmDinheiro, 'valor');

            const modalValorRecebidoTrocoDataClose = await this._modalValorRecebidoTrocoAsync(total);

            if (modalValorRecebidoTrocoDataClose.role === ModalRoleEnum.CANCEL) return;

            if (modalValorRecebidoTrocoDataClose.role === ModalRoleEnum.CONFIRM) {
                this.pedido.troco = modalValorRecebidoTrocoDataClose.data.troco;
                this.pedido.valorRecebido = modalValorRecebidoTrocoDataClose.data.valorRecebido;
            }
        }

        this.pedido.dets = [];

        this.pedido.codigoCabCaixa = this.codigoCaixa;
        this.pedido.terminalId = this.deviceService.device.idDispositivo;

        await this.datetimeService.getDateTime().toPromise().then(dataR => {
            this.pedido.dataHora = dataR.data as any;
        });

        const res = await firstValueFrom(this.pedidoService.fecharConta(this.pedido));

        if (res.success) {
            HelpersUtil.executeAfterDelay(async () => await this.confirm());
        }
    }

    public ionViewWillEnter(): void {
        this.carregarContaPagamentoComanda();
        HelpersUtil.executeAfterDelay(() => this.setFocusDescontoInp(), 1000);
    }

    public ionViewWillLeave(): void {
        this.subs$.forEach(sub$ => sub$.unsubscribe());
    }

    public async modalSelecionarClientes() {
        const modal = await this.modalCtrl.create({
            component: LocalizarClientesComponent,
            cssClass: 'full-screen-dialog'
        });

        await modal.present();

        DialogStateUtil.dialogIsOpen();

        const modalResult: ModalResult<LocalizarClientesDataClose> = await modal.onWillDismiss();

        DialogStateUtil.dialogIsClosed();

        if (modalResult.role === ModalRoleEnum.CONFIRM) {
            this.pedido.cliente = modalResult.data.cliente;

            const formaPgto = new FormaPgtoRequestV1();
            this.pedido.FormaPgtoOldV1 = formaPgto;
            this.pedido.terminalId = this.deviceService.device.idDispositivo;

            const datahora = await this.datetimeService.obterDataHora();
            this.pedido.dataHora = datahora;

            const sub$ = this.pedidoService.enviarPedido(this.pedido, TipoAtendimentoEnum.DELIVERY).subscribe();

            this.subs$.push(sub$);

            await modal.dismiss();
        }
    }

    public selecionouFormaPgto(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        this.setFocusInValorPagarInp();
    }

    public selecionouQtdPessoas(): void {
        this.rateioConta.totalPorPessoa = this.rateioConta.obterTotalPorPessoa();
    }

    @HostListener('window:keydown', ['$event'])
    public async handleKeyboardEvent(event: KeyboardEvent): Promise<void> {
        switch (event.key) {
            case 'F5':
                event.preventDefault();
                event.stopPropagation();

                if (!this.disableBtnFecharConta) {
                    this.fecharContaAsync();
                }

                break;
        }
    }

    private async _modalValorRecebidoTrocoAsync(valorAPagarEmReais: number) {
        return await this._modaisService.modalValorRecebidoTrocoAsync(valorAPagarEmReais);
    }

    private carregarContaPagamentoComanda() {
        const sub$ = this.pedidoService.obterContaPagamentoComanda(this.pedido.numeroComanda)
            .subscribe({
                next: (res) => {
                    if (!res.success || !res.data) {
                        return;
                    }

                    this.aplicarDadosContaPagamentoComanda(res.data);
                },
            });

        this.subs$.push(sub$);
    }

    private aplicarDadosContaPagamentoComanda(data: ContaPagamentoComandaResponse) {
        const formasPagamentoRaw = this.getField<any[]>(data, 'formasPagamento', 'FormasPagamento') ?? [];
        const itensPedido = this.getField<VendasDetalheModel[]>(data, 'itensPedido', 'ItensPedido') ?? [];
        const resumoPagamentosRaw = this.getField<any[]>(data, 'resumoPagamentos', 'ResumoPagamentos') ?? [];
        const valorPago = this.getField<number>(data, 'valorPago', 'ValorPago') ?? 0;
        const mesa = this.getField<any>(data, 'mesa', 'Mesa') ?? null;
        const cobrarTaxaServico = JSON.parse(data.configuracoesSistema.jsonOutrasConfig).ConfigsDelivery.CobrarTaxaServico

        const formasPagamento = formasPagamentoRaw.map((forma) => this.normalizarFormaPgto(forma));

        this.arrPgtos = resumoPagamentosRaw.map((pgto) => this.normalizarPgto(pgto));

        this.arrFormasPgto = formasPagamento;
        this.arrPedidos = itensPedido;

        if (formasPagamento.length > 0) {
            this.formaPgto = formasPagamento[0].codigo;
        }

        if (mesa?.grupo) {
            this.tipoAtendimento = mesa.grupo;
        }

        if (cobrarTaxaServico == true || cobrarTaxaServico == false) {
            this.rateioConta.incluirOpcional = cobrarTaxaServico && this.tipoAtendimento === TipoAtendimentoEnum.MESA;
        } else {
            throw new Error("Não foi possível obter 'cobrarTaxaService'");
        }

        this.rateioConta.valorPago = valorPago;

        this.prepararRateioConta(itensPedido);

        this.desativarEnvioPagamento = this.rateioConta.restantePagar.toFixed(1) === '0.0';
        this.disableBtnFecharConta = this.rateioConta.valorPago < this.rateioConta.subtotal;
    }

    private normalizarFormaPgto(forma: any): FormaPgtoResponse {
        return {
            nome: forma?.nome ?? forma?.forma ?? '',
            codigo: forma?.codigo ?? 0,
        };
    }

    private normalizarPgto(pgto: any): PgtoPedidoResponse {
        return {
            codigo: pgto?.codigo ?? 0,
            formaPgto: pgto?.formaPgto ?? pgto?.forma ?? '',
            operacao: pgto?.operacao ?? 0,
            valor: pgto?.valor ?? 0,
            nomeOperacao: pgto?.nomeOperacao
        };
    }

    private getField<T>(obj: any, camelName: string, pascalName: string): T | undefined {
        return obj?.[camelName] ?? obj?.[pascalName];
    }

    public validarValorAPagar() {
        if (this.valorPagar >= this.rateioConta.restantePagar) this.valorPagar = this.rateioConta.restantePagar
    }

    private resetarDesconto() {
        this.rateioConta.desconto = 0;
        this.calcularDesconto();
    }

    public calcularDesconto() {
        this.sincronizarRateioConta();
        this.valorPagar = this.rateioConta.restantePagar;
    }

    public calcularDescontoEAlterarFoco() {
        this.calcularDesconto();
        this.setFocusInFormaPgtoSel();
    }

    private adicionarPgtoLocalmente(data: any, request: PagarContaRequest, desconto: boolean = false) {
        const pagamentoResposta = Array.isArray(data) ? data[0] : data;
        const formaPgtoSel = this.arrFormasPgto.find(l => l.codigo === request.codigoFormaPgto);

        const operacao = desconto ? OperacaoPgtoEnum.Desconto : OperacaoPgtoEnum.Venda;
        const nomeOperacao = desconto ? "DECONTO" : "";
        const valor = desconto ? request.valorPago = -request.valorPago : request.valorPago;

        this.arrPgtos.push(this.normalizarPgto({
            codigo: pagamentoResposta?.codigo ?? pagamentoResposta?.Codigo ?? pagamentoResposta?.codigoVzc ?? pagamentoResposta?.CodigoVzc ?? request.codigoFormaPgto,
            formaPgto: formaPgtoSel.nome,
            operacao: operacao,
            nomeOperacao: nomeOperacao,
            valor: valor,
            NomeOperacao: pagamentoResposta.nomeOperacao ?? "",
        }));
    }

    private corrigeValorDescontoParaCalculo(arrPgtos: PgtoPedidoResponse[]) {
        return arrPgtos.map((p) => {
            if (p.operacao === OperacaoPgtoEnum.Desconto) {
                return {
                    ...p,
                    valor: -p.valor
                };
            }

            return p;
        });
    }

    private sincronizarRateioConta() {
        this.rateioConta.valorPago = HelpersUtil.sumArray(this.corrigeValorDescontoParaCalculo(this.arrPgtos), 'valor');
        this.rateioConta.restantePagar = this.rateioConta.obterRestantePagar();
        this.rateioConta.totalPorPessoa = this.rateioConta.obterTotalPorPessoa();

        this.desativarEnvioPagamento = this.rateioConta.restantePagar.toFixed(1) === '0.0';
        this.disableBtnFecharConta = this.rateioConta.valorPago < this.rateioConta.subtotal;
    }

    private prepararRateioConta(dets: VendasDetalheModel[]): void {
        const subtotal = HelpersUtil.sumArray(dets);
        let opcional = 0;

        if (this.configPedido.cobrarTaxaServico) {
            opcional = HelpersUtil.getPercentageValue(10, subtotal);
        }

        this.rateioConta.subtotal = subtotal;
        this.rateioConta.opcional = opcional;
        this.rateioConta.totalPorPessoa = this.rateioConta.obterTotalPorPessoa();
        this.rateioConta.restantePagar = this.rateioConta.obterRestantePagar();

        this.valorPagar = this.rateioConta.restantePagar;
    }

    private setFocusInFormaPgtoSel() {
        this.formaPgtoSel.nativeElement.focus();
    }

    private setFocusDescontoInp() {
        this.descontoInp.nativeElement.focus();
    }

    private setFocusInValorPagarInp() {
        this.valorPagarInp.nativeElement.focus();
    }
}
